import { AudioBufferSink, AudioBufferSource } from "mediabunny";

export type AudioExportItem = {
    startSec: number;
    inSec: number;
    outSec: number;
    getAudioTrack?: () => Promise<any>;
    gain?: number;
    rate?: number;
    kind?: "media" | "silence";
    sourceKey?: string;
};

export const TARGET_SAMPLE_RATE = 48000;
export const TARGET_CHANNELS = 2;

const EPS = 1e-3;

const makeSilence = (sec: number): AudioBuffer | null => {
    const frames = Math.max(0, Math.floor(sec * TARGET_SAMPLE_RATE));
    if (frames <= 0) return null;
    return new AudioBuffer({
        length: frames,
        numberOfChannels: TARGET_CHANNELS,
        sampleRate: TARGET_SAMPLE_RATE,
    });
};

const sliceBuffer = (buf: AudioBuffer, startSec: number, endSec: number): AudioBuffer | null => {
    const sr = buf.sampleRate;
    const sFrame = Math.max(0, Math.floor(startSec * sr));
    const eFrame = Math.min(buf.length, Math.floor(endSec * sr));
    const frames = Math.max(0, eFrame - sFrame);
    if (frames === 0) return null;
    const out = new AudioBuffer({
        length: frames,
        numberOfChannels: buf.numberOfChannels,
        sampleRate: sr,
    });
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
        const tmp = new Float32Array(frames);
        buf.copyFromChannel(tmp, ch, sFrame);
        out.copyToChannel(tmp, ch, 0);
    }
    return out;
};

const normalizeAudioBuffer = (buf: AudioBuffer, playbackRate = 1.0): AudioBuffer => {
    const srcRate = buf.sampleRate;
    const srcChannels = buf.numberOfChannels;
    const rate = Number(playbackRate) || 1.0;

    if (rate === 1.0 && srcRate === TARGET_SAMPLE_RATE && srcChannels === TARGET_CHANNELS) {
        return buf;
    }

    const ratio = (TARGET_SAMPLE_RATE / srcRate) / Math.max(0.001, rate);
    const outLength = Math.floor(buf.length * ratio);
    if (outLength === 0) return buf;

    const out = new AudioBuffer({
        length: outLength,
        numberOfChannels: TARGET_CHANNELS,
        sampleRate: TARGET_SAMPLE_RATE,
    });

    for (let ch = 0; ch < TARGET_CHANNELS; ch++) {
        const srcCh = Math.min(ch, srcChannels - 1);
        const srcData = buf.getChannelData(srcCh);
        const outData = out.getChannelData(ch);

        for (let i = 0; i < outLength; i++) {
            const srcIdx = i / ratio;
            const srcIdxFloor = Math.floor(srcIdx);
            const srcIdxCeil = Math.min(srcIdxFloor + 1, srcData.length - 1);
            const t = srcIdx - srcIdxFloor;
            outData[i] = srcData[srcIdxFloor] * (1 - t) + srcData[srcIdxCeil] * t;
        }
    }

    return out;
};

const mixInto = (
    dstL: Float32Array,
    dstR: Float32Array,
    src: AudioBuffer,
    dstStartFrame: number,
    gain: number,
): void => {
    if (!Number.isFinite(dstStartFrame) || dstStartFrame >= dstL.length) return;
    const start = Math.max(0, Math.floor(dstStartFrame));
    const maxFrames = Math.min(src.length, dstL.length - start, dstR.length - start);
    if (maxFrames <= 0) return;

    const left = src.getChannelData(0);
    const right = src.numberOfChannels > 1 ? src.getChannelData(1) : left;
    const g = Number.isFinite(gain) ? gain : 1.0;

    for (let i = 0; i < maxFrames; i++) {
        dstL[start + i] += left[i] * g;
        dstR[start + i] += right[i] * g;
    }
};

const softClip = (v: number): number => Math.tanh(v);

const overlapDuration = (item: AudioExportItem): number => {
    const srcDur = Math.max(0, Number(item.outSec ?? 0) - Number(item.inSec ?? 0));
    const rate = Math.max(0.001, Number(item.rate ?? 1));
    return srcDur / rate;
};

const sourceKeyFor = (item: AudioExportItem, idx: number): string => {
    return String(item.sourceKey ?? `item_${idx}`);
};

const getItemSinkFactory = (): ((item: AudioExportItem, index: number) => Promise<AudioBufferSink | null>) => {
    const sinkByKey = new Map<string, Promise<AudioBufferSink | null>>();

    return async (item: AudioExportItem, index: number): Promise<AudioBufferSink | null> => {
        if (item.kind === "silence" || typeof item.getAudioTrack !== "function") return null;
        const key = sourceKeyFor(item, index);
        let promise = sinkByKey.get(key);
        if (!promise) {
            promise = (async () => {
                try {
                    const track = await item.getAudioTrack!();
                    if (!track) return null;
                    const canDecode = await track.canDecode();
                    if (!canDecode) return null;
                    return new AudioBufferSink(track);
                } catch {
                    return null;
                }
            })();
            sinkByKey.set(key, promise);
        }
        return promise;
    };
};

const mixItemOverlapIntoWindow = async (args: {
    item: AudioExportItem;
    sink: AudioBufferSink;
    windowStartSec: number;
    windowEndSec: number;
    mixL: Float32Array;
    mixR: Float32Array;
    signal?: AbortSignal;
}): Promise<void> => {
    const { item, sink, windowStartSec, windowEndSec, mixL, mixR, signal } = args;
    const itemStart = Number(item.startSec ?? 0);
    const itemEnd = itemStart + overlapDuration(item);
    if (!(itemEnd > windowStartSec + EPS && itemStart < windowEndSec - EPS)) return;

    const overlapStart = Math.max(itemStart, windowStartSec);
    const overlapEnd = Math.min(itemEnd, windowEndSec);
    if (overlapEnd <= overlapStart + EPS) return;

    const rate = Math.max(0.001, Number(item.rate ?? 1));
    const srcStart = Number(item.inSec ?? 0) + (overlapStart - itemStart) * rate;
    const srcEnd = srcStart + (overlapEnd - overlapStart) * rate;
    if (!(Number.isFinite(srcStart) && Number.isFinite(srcEnd) && srcEnd > srcStart + EPS)) return;

    const gain = Number(item.gain ?? 1.0) || 1.0;

    let remainingSourceSec = srcEnd - srcStart;
    let dstFrameCursor = Math.max(0, Math.floor((overlapStart - windowStartSec) * TARGET_SAMPLE_RATE));

    for await (const wrapped of sink.buffers(srcStart, srcEnd)) {
        if (signal?.aborted) return;
        const buffer = wrapped?.buffer;
        if (!buffer) break;

        if (remainingSourceSec <= EPS) break;
        const bufferSourceDur = buffer.length / buffer.sampleRate;
        let toAdd = buffer;
        if (bufferSourceDur > remainingSourceSec + EPS) {
            const sliced = sliceBuffer(buffer, 0, remainingSourceSec);
            if (!sliced) break;
            toAdd = sliced;
        }

        const normalized = normalizeAudioBuffer(toAdd, rate);
        mixInto(mixL, mixR, normalized, dstFrameCursor, gain);

        dstFrameCursor += normalized.length;
        remainingSourceSec -= toAdd.length / toAdd.sampleRate;
    }
};

/**
 * Mixes timeline audio items into a single encoded track.
 * Unlike linear append, this supports true overlap mixing (same as preview behavior).
 */
export async function writeMixedAudioTrack(args: {
    audioSource: AudioBufferSource;
    items: AudioExportItem[];
    durationSec: number;
    signal?: AbortSignal;
    windowSec?: number;
}): Promise<void> {
    const { audioSource, signal } = args;
    const durationSec = Math.max(0, Number(args.durationSec ?? 0));
    const items = [...(args.items ?? [])]
        .filter((it) => Number.isFinite(it.startSec) && Number.isFinite(it.inSec) && Number.isFinite(it.outSec))
        .sort((a, b) => Number(a.startSec) - Number(b.startSec));

    const windowSec = Math.max(1, Math.floor(Number(args.windowSec ?? 20)));
    const getSink = getItemSinkFactory();

    if (durationSec <= EPS) return;
    if (signal?.aborted) return;

    // Fast path: no media at all -> emit a single silence span.
    const hasMedia = items.some((it) => it.kind !== "silence" && typeof it.getAudioTrack === "function");
    if (!hasMedia) {
        const silence = makeSilence(durationSec);
        if (silence) await audioSource.add(silence);
        return;
    }

    for (let windowStartSec = 0; windowStartSec < durationSec - EPS; windowStartSec += windowSec) {
        if (signal?.aborted) return;
        const windowEndSec = Math.min(durationSec, windowStartSec + windowSec);
        const windowFrames = Math.max(1, Math.ceil((windowEndSec - windowStartSec) * TARGET_SAMPLE_RATE));

        const mixL = new Float32Array(windowFrames);
        const mixR = new Float32Array(windowFrames);

        for (let i = 0; i < items.length; i++) {
            if (signal?.aborted) return;
            const item = items[i];
            if (item.kind === "silence" || typeof item.getAudioTrack !== "function") continue;

            const itemStart = Number(item.startSec ?? 0);
            const itemEnd = itemStart + overlapDuration(item);
            if (itemEnd <= windowStartSec + EPS || itemStart >= windowEndSec - EPS) continue;

            const sink = await getSink(item, i);
            if (!sink) continue;

            await mixItemOverlapIntoWindow({
                item,
                sink,
                windowStartSec,
                windowEndSec,
                mixL,
                mixR,
                signal,
            });
        }

        for (let i = 0; i < windowFrames; i++) {
            mixL[i] = softClip(mixL[i]);
            mixR[i] = softClip(mixR[i]);
        }

        const out = new AudioBuffer({
            length: windowFrames,
            numberOfChannels: TARGET_CHANNELS,
            sampleRate: TARGET_SAMPLE_RATE,
        });
        out.copyToChannel(mixL, 0, 0);
        out.copyToChannel(mixR, 1, 0);
        await audioSource.add(out);
    }
}
